import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";

export default function RoluriTab() {
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", label: "", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        erpApi.get("/roles"),
        erpApi.get("/permissions"),
      ]);
      setRoles(r.data);
      setGroups(p.data.groups);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", label: "", permissions: [] });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({ name: role.name, label: role.label, permissions: [...(role.permissions || [])] });
    setDialogOpen(true);
  };

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const toggleGroup = (groupPerms, allSelected) => {
    setForm((f) => {
      const set = new Set(f.permissions);
      groupPerms.forEach((k) => (allSelected ? set.delete(k) : set.add(k)));
      return { ...f, permissions: [...set] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await erpApi.put(`/roles/${editing.id}`, { name: form.name, label: form.label, permissions: form.permissions });
        toast.success("Rol actualizat");
      } else {
        await erpApi.post("/roles", form);
        toast.success("Rol creat");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await erpApi.delete(`/roles/${deleteTarget.id}`);
      toast.success("Rol șters");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4" data-testid="erp-roluri-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{roles.length} {roles.length === 1 ? "rol" : "roluri"}</p>
        <Button
          onClick={openCreate}
          data-testid="erp-add-role-button"
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Adaugă rol
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {roles.map((role) => (
          <div
            key={role.id}
            data-testid={`erp-role-card-${role.name}`}
            className="bg-white rounded-xl border border-slate-200 p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-[#2563EB]" />
                </span>
                <div>
                  <p className="font-semibold text-[#0B1424]">{role.label}</p>
                  <p className="text-xs text-slate-400 font-mono">{role.name}</p>
                </div>
              </div>
              {role.is_system ? (
                <Badge variant="secondary">Sistem</Badge>
              ) : (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" data-testid={`erp-edit-role-${role.name}`} onClick={() => openEdit(role)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`erp-delete-role-${role.name}`}
                    onClick={() => setDeleteTarget(role)}
                    className="text-[#DC2626] hover:text-[#DC2626] hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
              <span>{role.name === "admin" ? "Toate permisiunile" : `${role.permissions?.length || 0} ${(role.permissions?.length || 0) === 1 ? "permisiune" : "permisiuni"}`}</span>
              <span>·</span>
              <span>{role.users_count} {role.users_count === 1 ? "angajat" : "angajați"}</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editează rol" : "Rol nou"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Nume intern</Label>
                <Input
                  data-testid="erp-role-name"
                  value={form.name}
                  disabled={!!editing}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="mecanic"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="font-semibold">Denumire afișată</Label>
                <Input
                  data-testid="erp-role-label"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Mecanic"
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label className="font-semibold">Permisiuni</Label>
              {groups.map((g) => {
                const keys = g.permissions.map((p) => p.key);
                const allSelected = keys.every((k) => form.permissions.includes(k));
                return (
                  <div key={g.group} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-sm text-[#0B1424]">{g.group}</p>
                      <button
                        type="button"
                        onClick={() => toggleGroup(keys, allSelected)}
                        className="text-xs font-semibold text-[#2563EB] hover:underline"
                      >
                        {allSelected ? "Deselectează tot" : "Selectează tot"}
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {g.permissions.map((p) => (
                        <label
                          key={p.key}
                          className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"
                        >
                          <Checkbox
                            checked={form.permissions.includes(p.key)}
                            onCheckedChange={() => togglePerm(p.key)}
                            data-testid={`erp-perm-${p.key}`}
                            className="!rounded-[4px] border-[#2563EB] data-[state=checked]:bg-[#2563EB] data-[state=checked]:border-[#2563EB]"
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Anulează
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="erp-save-role-button"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi acest rol?</AlertDialogTitle>
            <AlertDialogDescription>
              Rolul „{deleteTarget?.label}" va fi șters. Angajații trebuie să nu mai aibă acest rol.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="erp-confirm-delete-role"
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
